import { useMemo } from 'react'

import { alpha, useTheme } from '@mui/material'

import { MotivationSource } from '../types'

/**
 * Visual palette used by the radar / bar charts to distinguish the three
 * motivation sources. Each source has a fully-opaque "stroke/bar" colour and a
 * matching translucent "fill" colour so the radar chart can layer fills
 * without obscuring the underlying gridlines.
 */
export interface MotivationSourceColor {
  /** Solid colour used for legends, lines, and bars. */
  stroke: string
  /** Translucent variant used for radar fill / hover tints. */
  fill: string
}

export type MotivationSourcePalette = Record<MotivationSource, MotivationSourceColor>

/**
 * Render order used so the most-often-referenced source (Creative Ads) sits
 * on top. The `gameDesign` series already folds in the live-events
 * signal (labelled "Game & Live Events"), so there are only two series in the
 * radar/bar charts.
 */
export const MOTIVATION_SOURCE_ORDER: readonly MotivationSource[] = ['gameDesign', 'creativeAds']

/** i18n keys for each source's display label, shared by the charts and their legend. */
export const MOTIVATION_SOURCE_LABEL_KEYS: Record<MotivationSource, string> = {
  gameDesign: 'motivation_source_game_design',
  creativeAds: 'motivation_source_creative_ads',
}

/**
 * Sources drawn with a dashed line / ring instead of a solid one. Currently
 * empty — the previously dashed `gameDesign` series is now the solid
 * "Game & Live Events" baseline. Kept as a set so a future source can opt back
 * in without reshaping the dataset builders.
 *
 * Shared with the legend deliberately: when the two kept their own copies, a
 * source could be opted in on the chart but not on the legend describing it.
 */
export const DASHED_MOTIVATION_SOURCES: ReadonlySet<MotivationSource> = new Set()

/** Chart.js `borderDash` pattern applied to {@link DASHED_MOTIVATION_SOURCES}. */
export const MOTIVATION_DASH_PATTERN: readonly number[] = [6, 4]

/**
 * Resolves the per-source palette from the active MUI theme so the chart
 * series stay in sync with the rest of the UI:
 *  - `gameDesign` → `secondary.main` (deep navy, dashed baseline reference)
 *  - `creativeAds` → `primary.main` (brand purple – the lead UA Insights series)
 *
 * Keeping the mapping behind a hook means a future palette tweak only has to
 * touch the theme, not every chart consumer. Fill opacities are intentionally
 * gentle so overlapping radar polygons stay readable.
 */
export function useMotivationSourceColors(): MotivationSourcePalette {
  const theme = useTheme()

  return useMemo<MotivationSourcePalette>(
    () => ({
      gameDesign: {
        stroke: theme.palette.secondary.main,
        fill: alpha(theme.palette.secondary.main, 0.06),
      },
      creativeAds: {
        stroke: theme.palette.primary.main,
        fill: alpha(theme.palette.primary.main, 0.16),
      },
    }),
    [theme]
  )
}
